/**
 * ONDE ESTE DESENHO ESTÁ NA PILHA — `T1.7`.
 *
 * Ele, na letra: *"Sistema de layers confuso (...) **não dá pra saber qual layer está ativa** nem
 * mexer nas camadas à direita; a layer só existe dentro do quadro."*
 *
 * ── O QUE FOI MEDIDO, EM 27/09, NO EDITOR DA VITRINE ────────────────────────────────────────
 *
 * Com um balão selecionado, o menu do item mostra dez controles, e dois deles são de pilha:
 * **"Mover para cima"** e **"Mover para baixo"**. Então mexer na ordem *dá*. O que não dá é saber
 * onde o item está: a palavra "camada" não aparece em lugar nenhum da tela — medido varrendo o
 * `innerText` inteiro do editor com o item selecionado —, os dois botões não mudam de aparência nos
 * extremos, e clicar "para cima" no item que já está na frente não faz nada e não diz nada.
 *
 * ── POR QUE ISTO E NÃO UM PAINEL DE CAMADAS ─────────────────────────────────────────────────
 *
 * O ponto pede *"seletor de camadas + área de montagem externa tipo Photoshop/Canva"*. A área
 * externa existe e foi medida em 25/09 (zoom, "Caber na tela"). O painel é a outra metade, e um
 * painel de camadas é uma tela nova num editor que uma criança de oito anos usa — decisão de
 * produto, não conserto.
 *
 * O que ESTE arquivo resolve é a frase exata da queixa: *não dá pra saber qual está ativa*. A
 * informação já existe (`comic.pages[n].items` é a ordem de desenho); ela só nunca foi dita. Dizer
 * o que já se sabe é conserto; inventar um painel é outro tamanho de trabalho, e fica escrito no nó.
 *
 * ── A ORDEM, E ELA É A DO KONVA ─────────────────────────────────────────────────────────────
 *
 * `items` é desenhado na ordem do array, e quem vem depois fica **na frente**. Então índice maior é
 * mais à frente, e é por isso que `moveEl(item, "up")` faz `index + 1`. A contagem que a tela mostra
 * segue a mesma direção: a camada 1 é a de trás, e a última é a da frente. Inverter aqui faria a
 * seta para cima diminuir o número na tela.
 */
// O TIPO QUE ENTRA E `Layer[]`, e nao `Comic`: `Comic.pages` e privado, e um modulo puro que
// precisasse do objeto inteiro nao daria para testar sem montar um editor. O `Comic` expoe
// `lugarNaPilha(item)`, que so repassa as proprias paginas para ca.
import { Layer } from "./layer.class";
import { DraggableItem } from "./draggable-item.class";

export type LugarNaPilha = {
  /** 1 é a camada de trás; `total` é a da frente. 0 quando o item não está em página nenhuma. */
  posicao: number;
  total: number;
  podeSubir: boolean;
  podeDescer: boolean;
};

/** Onde o item está na ordem de desenho da página dele. */
export function ondeNaPilha(paginas: Layer[] | undefined, item: DraggableItem): LugarNaPilha {
  const vazio: LugarNaPilha = { posicao: 0, total: 0, podeSubir: false, podeDescer: false };
  if (!paginas || !item) return vazio;

  const pagina = paginas.find((p) => !!p?.items?.find((i) => i?.id === item.id));
  if (!pagina || !pagina.items) return vazio;

  const indice = pagina.items.findIndex((i) => i?.id === item.id);
  if (indice === -1) return vazio;

  const total = pagina.items.length;
  return {
    posicao: indice + 1,
    total,
    podeSubir: indice + 1 < total,
    podeDescer: indice > 0,
  };
}

/**
 * O que a tela mostra: curto, porque o lugar é um menu de botões de 44 px.
 *
 * A PALAVRA ENTRA, e a primeira versão a tinha deixado de fora. O texto visível era "2 de 4" e a
 * palavra "camada" vivia só no título e no rótulo acessível — e a medição no editor perguntou se a
 * palavra aparecia na tela e respondeu NÃO, corretamente. A queixa do ponto é *"não dá pra saber
 * qual layer está ativa"*: um número sozinho, entre duas setas, não diz do que ele é a contagem.
 *
 * "camada 2/4" e não "camada 2 de 4": a barra economiza o suficiente para caber ao lado de dez
 * botões de 44 px, e é a forma que qualquer criança já viu num placar.
 */
export function fraseCurtaDaPilha(lugar: LugarNaPilha): string {
  if (!lugar.total) return "";
  return `camada ${lugar.posicao}/${lugar.total}`;
}

/**
 * A explicação, que vai no `title` e no rótulo acessível.
 *
 * Diz a direção, porque "camada 2 de 4" sozinho não responde se 4 é a da frente ou a de trás — e
 * essa é metade da confusão que o ponto descreve.
 */
export function explicacaoDaPilha(lugar: LugarNaPilha): string {
  if (!lugar.total) return "Este desenho não está em nenhuma página.";
  if (lugar.total === 1) return "Este é o único desenho desta página.";
  const onde =
    lugar.posicao === lugar.total
      ? "e está na frente de todos"
      : lugar.posicao === 1
        ? "e está atrás de todos"
        : `com ${lugar.total - lugar.posicao} na frente dele`;
  return `Camada ${lugar.posicao} de ${lugar.total} desta página, ${onde}. A de cima aparece na frente.`;
}

/** O rótulo de cada botão de pilha, já dizendo onde o item está e o que acontece. */
export function rotuloDeMover(lugar: LugarNaPilha, para: "cima" | "baixo"): string {
  if (!lugar.total || lugar.total === 1) return `Mover para ${para}`;
  const pode = para === "cima" ? lugar.podeSubir : lugar.podeDescer;
  if (!pode) {
    return para === "cima"
      ? `Já está na frente de todos (camada ${lugar.posicao} de ${lugar.total})`
      : `Já está atrás de todos (camada ${lugar.posicao} de ${lugar.total})`;
  }
  const destino = para === "cima" ? lugar.posicao + 1 : lugar.posicao - 1;
  return `Mover para ${para}: da camada ${lugar.posicao} para a ${destino}, de ${lugar.total}`;
}
